import { ErrorInfo } from 'react';
import { Box, Button, Container, Typography, Paper } from '@mui/material';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../router/routes';
import { getFriendlyError } from '../../lib/errorMessages';

interface FallbackUIProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  resetError: () => void;
}

/**
 * FallbackUI Component
 *
 * User-friendly error display shown when an error boundary catches an error.
 * Provides options to retry or navigate home.
 */
function FallbackUI({ error, errorInfo, resetError }: FallbackUIProps) {
  const navigate = useNavigate();
  const isDevelopment = import.meta.env.DEV;

  const friendly = getFriendlyError(error?.message ?? '');

  const handleGoHome = () => {
    resetError();
    navigate(ROUTES.HOME);
  };

  const handleRetry = () => {
    resetError();
    window.location.reload();
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            width: '100%',
            maxWidth: 600,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <AlertTriangle size={64} color="#f39c12" />
          </Box>

          {/* Customer-friendly title */}
          <Typography variant="h4" component="h1" gutterBottom>
            {friendly.title}
          </Typography>

          {/* Customer-friendly description — no technical jargon */}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {friendly.description}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshCw size={20} />}
              onClick={handleRetry}
              sx={{ minWidth: 150 }}
            >
              {friendly.action ?? 'Try Again'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Home size={20} />}
              onClick={handleGoHome}
              sx={{ minWidth: 150 }}
            >
              Go to Dashboard
            </Button>
          </Box>

          {/* Technical details only in development — never shown to customers */}
          {isDevelopment && error && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mt: 3,
                textAlign: 'left',
                bgcolor: 'grey.100',
                overflow: 'auto',
                maxHeight: 300,
              }}
            >
              <Typography variant="subtitle2" color="error" sx={{ fontWeight: 'bold', mb: 1 }}>
                Developer Details (dev only):
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: 2 }}
              >
                {error.toString()}
              </Typography>
              {errorInfo?.componentStack && (
                <>
                  <Typography variant="subtitle2" color="error" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Component Stack:
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {errorInfo.componentStack}
                  </Typography>
                </>
              )}
            </Paper>
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default FallbackUI;
